import { useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useProfiles } from "../profiles/ProfileContext";
import { Button } from "../components/ui/Button";
import { PageHeader, Panel } from "../components/Surface";
import { Icon } from "../app/Icon";
import { inputClass, labelClass } from "../components/FormKit";
import { updateProfile, PROFILE_TYPE_LABELS } from "../lib/profiles";
import { status2fa, enroll2fa, verify2fa, disable2fa, emailMfaStatus, emailMfaSend, emailMfaEnable, emailMfaDisable, type TwoFactorStatus, type EnrollResult } from "../lib/twofa";
import { saveSession, loadSession } from "../lib/auth-store";
import { ActiveSessions } from "../components/ActiveSessions";
import { getPrivacy, updatePrivacy, VISIBILITY_LEVELS, type PrivacySettings, type Visibility } from "../lib/privacy";
import { useMode } from "../mode/ModeContext";
import { ModeToggle } from "../components/ModeToggle";
import { resetSandbox } from "../lib/sandbox";

const SECTIONS = [
  { key: "profile", label: "Profile", icon: "profile" },
  { key: "account", label: "Account", icon: "applications" },
  { key: "security", label: "Security", icon: "security" },
  { key: "privacy", label: "Privacy", icon: "privacy" },
  { key: "notifications", label: "Notifications", icon: "bell" },
  { key: "developers", label: "Developers", icon: "settings" },
] as const;

type SectionKey = (typeof SECTIONS)[number]["key"];

export function Settings() {
  const { section } = useParams();
  const active: SectionKey = (SECTIONS.find((s) => s.key === section)?.key ?? "profile") as SectionKey;

  return (
    <div className="grid gap-6">
      <PageHeader title="Settings" subtitle="Manage your account, preferences, and security." />

      <div className="grid gap-6 lg:grid-cols-[210px_minmax(0,1fr)_280px]">
        {/* Sub-nav */}
        <nav className="grid h-max content-start gap-1 rounded-card border border-line bg-surface p-2">
          {SECTIONS.map((s) => (
            <Link
              key={s.key}
              to={`/settings/${s.key}`}
              aria-current={active === s.key ? "page" : undefined}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                active === s.key ? "bg-accent text-accent-ink" : "text-muted hover:bg-surface-2 hover:text-ink"
              }`}
            >
              <Icon name={s.icon} />
              {s.label}
            </Link>
          ))}
        </nav>

        {/* Section content */}
        <div className="grid content-start gap-6">
          {active === "profile" && <ProfileSection />}
          {active === "account" && <AccountSection />}
          {active === "security" && <SecuritySection />}
          {active === "privacy" && <PrivacySection />}
          {active === "notifications" && <NotificationsSection />}
          {active === "developers" && <DevelopersSection />}
        </div>

        {/* Summary rail */}
        <SummaryRail />
      </div>
    </div>
  );
}

// ── Profile ─────────────────────────────────────────────────────────────────
function ProfileSection() {
  const { active, refresh } = useProfiles();
  const [displayName, setDisplayName] = useState("");
  const [headline, setHeadline] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDisplayName(active?.display_name ?? "");
    setHeadline(active?.headline ?? "");
  }, [active?.id, active?.display_name, active?.headline]);

  if (!active) {
    return (
      <Panel title="Profile">
        <p className="text-sm text-muted">
          No active profile. <Link to="/profiles/new" className="font-bold text-accent-strong">Create one →</Link>
        </p>
      </Panel>
    );
  }

  async function save() {
    if (!active) return;
    setBusy(true);
    setNote(null);
    try {
      await updateProfile({ id: active.id, display_name: displayName, headline });
      await refresh();
      setNote("Saved.");
    } catch {
      setNote("Could not save.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Profile information" action={<span className="font-mono text-xs text-muted">{PROFILE_TYPE_LABELS[active.type]}</span>}>
      <p className="text-sm text-muted">Your public display name and headline for this profile.</p>
      <label className={labelClass}>
        Display name
        <input className={inputClass} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
      </label>
      <label className={labelClass}>
        Headline
        <input className={inputClass} placeholder="Class A OTR · 6 yrs · Hazmat" value={headline} onChange={(e) => setHeadline(e.target.value)} />
      </label>
      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={busy}>{busy ? "Saving…" : "Save changes"}</Button>
        <Link to={`/profiles/${active.id}/edit`} className="text-sm font-bold text-accent-strong">Edit full profile →</Link>
        {note && <span className="font-mono text-sm text-muted">{note}</span>}
      </div>
    </Panel>
  );
}

// ── Account ─────────────────────────────────────────────────────────────────
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-t border-line py-3 first:border-t-0 first:pt-0">
      <span className="text-sm text-muted">{label}</span>
      <span className="font-mono text-sm text-ink">{children}</span>
    </div>
  );
}

function AccountSection() {
  const { account, roles } = useAuth();
  const verified = account?.identity_verified;
  return (
    <>
      <Panel title="Account information">
        <div>
          <Row label="Email">{account?.email ?? "—"}</Row>
          <Row label="Account status">{account?.status ?? "active"}</Row>
          <Row label="Identity">
            {verified ? (
              <span className="rounded-full bg-verified-bg px-2 py-0.5 text-xs font-bold text-verified">VERIFIED</span>
            ) : (
              <Link to="/verify" className="font-bold text-accent-strong">Verify now →</Link>
            )}
          </Row>
          <Row label="Roles">{roles.length ? roles.join(", ") : "member"}</Row>
          <Row label="Member since">{account?.created_at ? new Date(account.created_at).toLocaleDateString() : "—"}</Row>
        </div>
      </Panel>
      <Panel title="Password & email">
        <p className="text-sm text-muted">
          To change your password, use “Forgot password?” on the sign-in page. Email changes are handled by support for now.
        </p>
      </Panel>
    </>
  );
}

// ── Security (2FA) ───────────────────────────────────────────────────────────
function qrSrc(qr: string): string {
  return qr.startsWith("data:") ? qr : `data:image/svg+xml;utf8,${encodeURIComponent(qr)}`;
}

function StepDots({ step, total }: { step: number; total: number }) {
  return (
    <div className="flex items-center gap-2 font-mono text-xs text-muted">
      {Array.from({ length: total }, (_, i) => i + 1).map((n) => (
        <span key={n} className="flex items-center gap-2">
          <span className={`grid h-6 w-6 place-items-center rounded-full border ${n <= step ? "border-accent bg-accent/15 text-accent-strong" : "border-line"}`}>{n}</span>
          {n < total && <span className={`h-px w-6 ${n < step ? "bg-accent" : "bg-line"}`} />}
        </span>
      ))}
      <span className="ml-1">Step {step} of {total}</span>
    </div>
  );
}

function SecuritySection() {
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [emailOn, setEmailOn] = useState(false);
  const [method, setMethod] = useState<"totp" | "email" | null>(null);
  const [enroll, setEnroll] = useState<EnrollResult | null>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [code, setCode] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    const [s, e] = await Promise.all([status2fa(), emailMfaStatus()]);
    setStatus(s);
    setEmailOn(e.enabled);
  }
  useEffect(() => { void refresh(); }, []);

  function cancel() {
    setMethod(null);
    setEnroll(null);
    setCode("");
    setStep(1);
  }
  async function startTotp() {
    setBusy(true); setNote(null); setMethod("totp"); setStep(1);
    try {
      setEnroll(await enroll2fa());
    } catch {
      setNote("Couldn't start setup. Please try again."); setMethod(null);
    } finally { setBusy(false); }
  }
  async function onVerify() {
    if (!enroll) return;
    setBusy(true); setNote(null);
    try {
      const res = await verify2fa(enroll.factor_id, code);
      // Verifying elevates the session to aal2. Persist the new tokens, otherwise the
      // (now aal1) session is rejected the instant the account has a verified factor —
      // which would 401 the very next request and log the user out after enrolling.
      if (res.access_token && res.refresh_token) {
        const prev = loadSession();
        saveSession({ access_token: res.access_token, refresh_token: res.refresh_token, user_id: prev?.user_id ?? "" });
      }
      cancel(); await refresh();
      setNote("Two-factor authentication is on.");
    } catch {
      setNote("That code didn't match. Enter the current 6-digit code from your app.");
    } finally { setBusy(false); }
  }
  async function startEmail() {
    setBusy(true); setNote(null); setMethod("email"); setStep(1); setCode("");
    try {
      await emailMfaSend();
    } catch {
      setNote("Email codes aren't available yet. Try the authenticator app."); setMethod(null);
    } finally { setBusy(false); }
  }
  async function onEmailEnable() {
    setBusy(true); setNote(null);
    try {
      await emailMfaEnable(code);
      cancel(); await refresh();
      setNote("Email two-factor authentication is on.");
    } catch {
      setNote("That code didn't match or expired. Check your email and try again.");
    } finally { setBusy(false); }
  }
  async function onDisableTotp(id: string) {
    setBusy(true);
    try { await disable2fa(id); await refresh(); setNote("Two-factor authentication is off."); }
    finally { setBusy(false); }
  }
  async function onDisableEmail() {
    setBusy(true);
    try { await emailMfaDisable(); await refresh(); setNote("Two-factor authentication is off."); }
    finally { setBusy(false); }
  }

  const verified = status?.factors.filter((f) => f.status === "verified") ?? [];
  const anyOn = Boolean(status?.enabled) || emailOn;

  return (
    <div className="grid gap-6">
      <Panel title="Two-factor authentication">
        {anyOn ? (
          // ── Enabled ──
          <div className="grid gap-3">
            <p className="flex items-center gap-2 font-mono text-sm text-verified">
              <Icon name="verify" className="h-4 w-4" /> On — a {status?.enabled ? "code from your authenticator app" : "code emailed to you"} is required at every sign-in.
            </p>
            {status?.enabled
              ? verified.map((f) => (
                  <div key={f.id}><Button variant="ghost" onClick={() => onDisableTotp(f.id)} disabled={busy}>Turn off two-factor</Button></div>
                ))
              : <div><Button variant="ghost" onClick={onDisableEmail} disabled={busy}>Turn off two-factor</Button></div>}
          </div>
        ) : method === "totp" ? (
          // ── Authenticator wizard ──
          <div className="grid gap-4">
            <StepDots step={step} total={2} />
            {step === 1 ? (
              <div className="grid gap-3">
                <p className="font-bold">Step 1 — Scan the QR code</p>
                <p className="text-sm text-muted">Open your authenticator app (Google Authenticator, Authy, 1Password…) and scan this. Can't scan? Enter the secret manually.</p>
                {enroll ? (
                  <>
                    <img src={qrSrc(enroll.qr_code)} alt="Authenticator QR code" className="h-44 w-44 rounded-lg bg-white p-2" />
                    <p className="break-all font-mono text-xs text-muted">Secret: <span data-testid="totp-secret">{enroll.secret}</span></p>
                  </>
                ) : <p className="font-mono text-sm text-muted">Preparing…</p>}
                <div className="flex gap-2">
                  <Button onClick={() => setStep(2)} disabled={!enroll}>I've added it — continue</Button>
                  <Button variant="ghost" onClick={cancel}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="grid gap-3">
                <p className="font-bold">Step 2 — Enter the 6-digit code</p>
                <p className="text-sm text-muted">Type the current code from your authenticator app to confirm and turn on two-factor.</p>
                <div className="flex items-center gap-2">
                  <input className="w-36 rounded-lg border border-line bg-surface-2 px-3 py-2 text-center font-mono text-lg tracking-[0.3em]" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" autoFocus />
                  <Button onClick={onVerify} disabled={busy || code.length < 6}>{busy ? "Verifying…" : "Turn on"}</Button>
                </div>
                <div><button type="button" className="text-sm font-bold text-muted hover:text-ink" onClick={() => setStep(1)}>← Back to the QR code</button></div>
              </div>
            )}
          </div>
        ) : method === "email" ? (
          // ── Email-code setup ──
          <div className="grid gap-3">
            <p className="font-bold">Check your email</p>
            <p className="text-sm text-muted">We sent a 6-digit code to your account email. Enter it to turn on email two-factor.</p>
            <div className="flex items-center gap-2">
              <input className="w-36 rounded-lg border border-line bg-surface-2 px-3 py-2 text-center font-mono text-lg tracking-[0.3em]" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" autoFocus />
              <Button onClick={onEmailEnable} disabled={busy || code.length < 6}>{busy ? "Verifying…" : "Turn on"}</Button>
            </div>
            <div className="flex gap-3">
              <button type="button" className="text-sm font-bold text-muted hover:text-ink" onClick={startEmail} disabled={busy}>Resend code</button>
              <button type="button" className="text-sm font-bold text-muted hover:text-ink" onClick={cancel}>Cancel</button>
            </div>
          </div>
        ) : (
          // ── Choose a method ──
          <div className="grid gap-3">
            <p className="text-sm text-muted">Add a second step at sign-in so your password alone can't unlock your account. Choose a method:</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <button type="button" onClick={startTotp} disabled={busy} className="grid gap-1 rounded-card border border-line bg-surface-2 p-4 text-left transition-colors hover:border-accent disabled:opacity-60">
                <span className="flex items-center gap-2 font-bold"><Icon name="security" className="h-4 w-4 text-accent-strong" /> Authenticator app</span>
                <span className="text-sm text-muted">Use Google Authenticator, Authy, or 1Password to generate codes. Works offline. Recommended.</span>
              </button>
              <button type="button" onClick={startEmail} disabled={busy} className="grid gap-1 rounded-card border border-line bg-surface-2 p-4 text-left transition-colors hover:border-accent disabled:opacity-60">
                <span className="flex items-center gap-2 font-bold"><Icon name="bell" className="h-4 w-4 text-accent-strong" /> Email code</span>
                <span className="text-sm text-muted">Get a one-time code by email at each sign-in. No app to install.</span>
              </button>
            </div>
          </div>
        )}
        {note && <p className="font-mono text-sm text-muted">{note}</p>}
      </Panel>
      <ActiveSessions />
    </div>
  );
}

// ── Privacy ─────────────────────────────────────────────────────────────────
function PrivacySection() {
  const [settings, setSettings] = useState<PrivacySettings | null>(null);
  const [realName, setRealName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getPrivacy().then((r) => {
      setSettings(r.settings);
      setRealName(r.contact.real_name ?? "");
      setPhone(r.contact.phone ?? "");
      setEmail(r.contact.email ?? "");
    });
  }, []);

  if (!settings) return <Panel title="Privacy"><p className="font-mono text-sm text-muted">Loading…</p></Panel>;

  const setVis = (field: keyof PrivacySettings, v: Visibility) => setSettings((s) => (s ? { ...s, [field]: v } : s));
  const visSelect = (field: keyof PrivacySettings) => (
    <select className={inputClass} value={settings[field]} onChange={(e) => setVis(field, e.target.value as Visibility)}>
      {VISIBILITY_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
    </select>
  );

  async function save() {
    if (!settings) return;
    setBusy(true);
    setNote(null);
    try {
      await updatePrivacy({ ...settings, real_name: realName, phone });
      setNote("Saved.");
    } catch {
      setNote("Could not save.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Privacy">
      <p className="text-sm text-muted">
        Your display name is always shown. Real details are hidden by default — choose who sees each.
      </p>
      <label className={labelClass}>
        Real name
        <input className={inputClass} value={realName} onChange={(e) => setRealName(e.target.value)} />
      </label>
      <label className={labelClass}>Name visible to{visSelect("name_visibility")}</label>
      <div className="grid gap-1">
        <span className="text-xs font-bold text-muted">Email (your login)</span>
        <input className={`${inputClass} opacity-60`} value={email} readOnly />
      </div>
      <label className={labelClass}>Email visible to{visSelect("email_visibility")}</label>
      <label className={labelClass}>
        Phone
        <input className={inputClass} value={phone} onChange={(e) => setPhone(e.target.value)} />
      </label>
      <label className={labelClass}>Phone visible to{visSelect("phone_visibility")}</label>
      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={busy}>{busy ? "Saving…" : "Save changes"}</Button>
        {note && <span className="font-mono text-sm text-muted">{note}</span>}
      </div>
    </Panel>
  );
}

// ── Notifications (placeholder) ──────────────────────────────────────────────
function NotificationsSection() {
  const channels = ["New messages", "Job applications", "Load bids", "Verification updates", "Connection requests"];
  return (
    <Panel title="Notifications" action={<span className="rounded-full bg-surface-2 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest text-muted">Coming soon</span>}>
      <p className="text-sm text-muted">
        Choose how you're notified. Delivery controls are coming soon — for now, in-app notifications are always on.
      </p>
      <ul className="grid gap-2">
        {channels.map((c) => (
          <li key={c} className="flex items-center justify-between border-t border-line py-2.5 first:border-t-0 first:pt-0">
            <span className="text-sm">{c}</span>
            <span className="grid h-5 w-9 place-items-start rounded-full bg-accent/60 p-0.5" aria-hidden>
              <span className="h-4 w-4 translate-x-4 rounded-full bg-surface" />
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

// ── Developers / Test mode ───────────────────────────────────────────────────
function DevelopersSection() {
  const { isTest, busy } = useMode();
  const [note, setNote] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);

  async function repopulate() {
    if (!window.confirm("Replace your test data with a fresh set? Your current sandbox changes will be lost.")) return;
    setResetting(true);
    setNote(null);
    try {
      await resetSandbox();
      setNote("Test data repopulated. Reloading…");
      window.location.reload();
    } catch {
      setNote("Couldn't repopulate test data. Please try again.");
      setResetting(false);
    }
  }

  return (
    <Panel title="Test mode">
      <p className="text-sm text-muted">
        Test mode is your private sandbox. Post jobs and loads, bid, apply, and explore with fake data — nothing
        here affects the live marketplace or other users, and you're never charged. Learn RecruitTrucker with zero risk.
      </p>
      <div className="flex flex-wrap items-center gap-3 border-t border-line pt-3">
        <span className="text-sm font-semibold">Data mode</span>
        <ModeToggle />
        <span className="font-mono text-[11px] text-muted">Currently: {isTest ? "TEST (sandbox)" : "LIVE"}</span>
      </div>
      {isTest ? (
        <div className="grid gap-2 border-t border-line pt-3">
          <p className="text-sm text-muted">Made a mess experimenting? Start over with a fresh set of sample data.</p>
          <Button onClick={repopulate} disabled={resetting || busy} className="justify-self-start">
            {resetting ? "Repopulating…" : "Repopulate test data"}
          </Button>
        </div>
      ) : (
        <p className="border-t border-line pt-3 text-sm text-muted">
          Switch to <b>Test</b> above to enter the sandbox — we'll set up starter data for you automatically.
        </p>
      )}
      {note && <p className="font-mono text-[11px] text-verified">{note}</p>}
    </Panel>
  );
}

// ── Summary rail ─────────────────────────────────────────────────────────────
function SummaryRail() {
  const { account, roles, logout } = useAuth();
  const { active } = useProfiles();
  return (
    <div className="grid content-start gap-6">
      <Panel title="Account summary">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-full bg-accent/15 font-display font-black text-accent-strong">
            {(account?.email ?? "?").slice(0, 1).toUpperCase()}
          </span>
          <div className="grid">
            <span className="truncate text-sm font-bold">{account?.email ?? "—"}</span>
            <span className="font-mono text-xs text-muted">{roles.length ? roles.join(" · ") : "member"}</span>
          </div>
        </div>
        <div className="font-mono text-xs text-muted">
          {account?.identity_verified ? (
            <span className="text-verified">● Identity verified</span>
          ) : (
            <Link to="/verify" className="text-accent-strong">Verify your identity →</Link>
          )}
        </div>
      </Panel>

      <Panel title="Quick actions">
        <div className="grid gap-1">
          {active && <RailLink to={`/profiles/${active.id}/edit`} icon="profile" label="Edit full profile" />}
          <RailLink to="/verify" icon="verify" label="Verification" />
          <RailLink to="/connections" icon="connections" label="Connections" />
          <button
            type="button"
            onClick={() => void logout()}
            className="flex items-center justify-between rounded-lg px-2 py-2 text-sm font-semibold text-danger hover:bg-surface-2"
          >
            <span className="flex items-center gap-2"><Icon name="logout" className="h-4 w-4" /> Sign out</span>
          </button>
        </div>
      </Panel>

      <Panel title="Need help?">
        <p className="text-sm text-muted">Questions about your account or verification? We're here to help.</p>
        <Link to="/messages" className="text-sm font-bold text-accent-strong">Contact support →</Link>
      </Panel>
    </div>
  );
}

function RailLink({ to, icon, label }: { to: string; icon: string; label: string }) {
  return (
    <Link to={to} className="flex items-center justify-between rounded-lg px-2 py-2 text-sm font-semibold text-muted transition-colors hover:bg-surface-2 hover:text-ink">
      <span className="flex items-center gap-2"><Icon name={icon} className="h-4 w-4" /> {label}</span>
      <span className="text-muted">›</span>
    </Link>
  );
}
